// @flow
import 'isomorphic-fetch';
import React from 'react';
import Cookies from 'universal-cookie';
import Header from '@ff/ui/Header';
import Header2 from '@ff/ui/Header2';
import Header3 from '@ff/ui/Header3';
import { mapLoggedInLabel } from '@ff/ui/Header2/utils';
import { mapLoggedInLabel as mapLoggedInLabel3 } from '@ff/ui/Header3/utils';
import { zIndex } from 'shared/constants';
import { IS_APP } from 'shared/lib/pathUtils';
import { styled } from '@ff/ui';
import isIPArabicRegion from 'shared/lib/isIPArabicRegion';
import { getRedirectForAR, getWebDomain } from 'shared/lib/clientSettings';

export default function MenuBar(props) {
  const cookies = new Cookies();
  const isLoggedIn = cookies.get('isLoggedIn') === 'true';

  const {
    version,
    scroll_effect,
    logo_link,
    padding,
    color_theme,
    drawerLinks,
    centerLinks,
    menuLinks,
    logoType,
    customLogo,
    transparent,
    float,
    spacer,
    background,
    button,
    hamburgerMenuColor,
    appOnlySettings,
    menuBarHeight,
  } = props;
  
  // Extract background and dropdown animation props (support both old flat structure and new nested structure)
  // Handle nested object structure: background.gradient, background.dropdownAnimationDuration, etc.
  const backgroundGradient = (background && typeof background === 'object' && background.gradient) 
    ? background.gradient 
    : (typeof background === 'string' ? background : undefined);
  
  // Extract dropdown animation duration - check nested object first, then flat props, handle empty strings
  // Automatically appends "s" if the value is a number without a unit
  const getDropdownAnimationDuration = () => {
    let rawValue = undefined;
    
    // Check nested structure first (new format)
    if (background && typeof background === 'object' && background !== null && !Array.isArray(background)) {
      const nestedValue = background.dropdownAnimationDuration;
      if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
        rawValue = String(nestedValue).trim();
      }
    }
    
    // Fall back to flat props (old format or direct props) if nested value not found
    if (!rawValue) {
      const flatValue = props.dropdownAnimationDuration;
      if (flatValue !== undefined && flatValue !== null && flatValue !== '') {
        rawValue = String(flatValue).trim();
      }
    }
    
    // Process the value: append "s" if it's a number without a unit
    if (rawValue) {
      // Check if value already ends with "s" or "ms" (has a unit)
      const hasUnit = /[sm]$/i.test(rawValue);
      if (!hasUnit) {
        // Check if it's a valid number (including decimals)
        const isNumber = /^\d+(\.\d+)?$/.test(rawValue);
        if (isNumber) {
          // Append "s" to the number
          return rawValue + 's';
        }
      }
      // Return as-is if it already has a unit or is not a simple number
      return rawValue;
    }
    
    return undefined;
  };
  
  // Extract dropdown animation timing - check nested object first, then flat props, handle empty strings
  const getDropdownAnimationTiming = () => {
    // Check nested structure first (new format)
    if (background && typeof background === 'object' && background !== null && !Array.isArray(background)) {
      const nestedValue = background.dropdownAnimationTiming;
      if (nestedValue !== undefined && nestedValue !== null && nestedValue !== '') {
        const value = String(nestedValue).trim();
        if (value) {
          return value;
        }
      }
    }
    // Fall back to flat props (old format or direct props)
    const flatValue = props.dropdownAnimationTiming;
    if (flatValue !== undefined && flatValue !== null && flatValue !== '') {
      const value = String(flatValue).trim();
      if (value) {
        return value;
      }
    }
    return undefined;
  };
  
  const dropdownAnimationDuration = getDropdownAnimationDuration();
  const dropdownAnimationTiming = getDropdownAnimationTiming();
  
  // Debug logging (remove in production if needed)
  if (process.env.NODE_ENV === 'development') {
    console.log('MenuBar dropdown animation props:', {
      background,
      dropdownAnimationDuration,
      dropdownAnimationTiming,
      backgroundType: typeof background,
      isObject: background && typeof background === 'object',
    });
  }
  
  // Extract dropdown extension height - check nested object first, then flat props, handle empty strings
  const getDropdownExtensionHeight = () => {
    // Check nested structure first (new format)
    if (background && typeof background === 'object' && background !== null) {
      if (background.dropdownExtensionHeight !== undefined && background.dropdownExtensionHeight !== null) {
        const value = background.dropdownExtensionHeight;
        if (typeof value === 'string' && value.trim()) {
          return value.trim();
        }
      }
    }
    // Fall back to flat props (old format or direct props)
    if (props.dropdownExtensionHeight !== undefined && props.dropdownExtensionHeight !== null) {
      const value = props.dropdownExtensionHeight;
      if (typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
    return undefined;
  };
  
  const dropdownExtensionHeight = getDropdownExtensionHeight();
  
  // Extract menu height - check menuBarHeight object first, then flat props
  const getMenuHeight = () => {
    // Check nested structure first (menuBarHeight.desktop)
    if (menuBarHeight && typeof menuBarHeight === 'object' && menuBarHeight !== null) {
      if (menuBarHeight.desktop !== undefined && menuBarHeight.desktop !== null) {
        const value = menuBarHeight.desktop;
        if (typeof value === 'string' && value.trim()) {
          const parsed = parseInt(value.trim(), 10);
          if (!isNaN(parsed)) return parsed;
        }
        if (typeof value === 'number') return value;
      }
    }
    // Fall back to flat props (old format or direct props)
    if (props.menuHeight !== undefined && props.menuHeight !== null) {
      const value = props.menuHeight;
      if (typeof value === 'string' && value.trim()) {
        const parsed = parseInt(value.trim(), 10);
        if (!isNaN(parsed)) return parsed;
      }
      if (typeof value === 'number') return value;
    }
    return undefined;
  };
  
  // Extract mobile menu height - check menuBarHeight object first, then flat props
  const getMobileMenuHeight = () => {
    // Check nested structure first (menuBarHeight.mobile)
    if (menuBarHeight && typeof menuBarHeight === 'object' && menuBarHeight !== null) {
      if (menuBarHeight.mobile !== undefined && menuBarHeight.mobile !== null) {
        const value = menuBarHeight.mobile;
        if (typeof value === 'string' && value.trim()) {
          const parsed = parseInt(value.trim(), 10);
          if (!isNaN(parsed)) return parsed;
        }
        if (typeof value === 'number') return value;
      }
    }
    // Fall back to flat props (old format or direct props)
    if (props.mobileMenuHeight !== undefined && props.mobileMenuHeight !== null) {
      const value = props.mobileMenuHeight;
      if (typeof value === 'string' && value.trim()) {
        const parsed = parseInt(value.trim(), 10);
        if (!isNaN(parsed)) return parsed;
      }
      if (typeof value === 'number') return value;
    }
    return undefined;
  };
  
  const menuHeight = getMenuHeight();
  const mobileMenuHeight = getMobileMenuHeight();
  const isEditor = !!props.editor;

  const showInAppOnly = appOnlySettings?.showInAppOnly;
  const topOffset = appOnlySettings?.topOffset;

  // Allow menu to be shown even when APPONLY=1 if showInAppOnly is true
  if (IS_APP && !showInAppOnly) return null;

  // Only apply topOffset when APPONLY=1 is in URL AND showInAppOnly is checked
  const shouldApplyTopOffset = IS_APP && showInAppOnly && topOffset;
  const finalTopOffset = shouldApplyTopOffset ? topOffset : undefined;

  /** version 3 */
  if (version == 3) { // version is string
    return (
      <Container isEditor={isEditor}> 
        <Header3
          scrollEffect={scroll_effect}
          colorTheme={color_theme}
          logoLink={logo_link}
          drawerLinks={mapLoggedInLabel3(drawerLinks, isLoggedIn)}
          menuLinks={mapLoggedInLabel3(menuLinks, isLoggedIn)}
          logoType={logoType}
          customLogo={customLogo}
          zIndex={zIndex.MENU_BAR}
          drawerZIndex={zIndex.MENU_BAR + 1}
          transparent={transparent}
          float={float}
          spacer={spacer}
          background={backgroundGradient}
          hamburgerMenuColor={hamburgerMenuColor}
          topOffset={finalTopOffset}
          padding={padding}
          dropdownAnimationDuration={dropdownAnimationDuration}
          dropdownAnimationTiming={dropdownAnimationTiming}
          dropdownExtensionHeight={dropdownExtensionHeight ? parseInt(dropdownExtensionHeight, 10) : undefined}
          menuHeight={menuHeight}
          mobileMenuHeight={mobileMenuHeight}
        />
      </Container>
    );
  }

  /** version 2 */
  if (version == 2) { // version is string
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      // Only support redirection for the homepage
      if (isRootPath(url) && !containsArabicPath(url) && !shouldIgnoreRedirect(url, props)) {
        shouldRedirectToArabic().then((redirectToArabic) => {
          if (redirectToArabic) {
            const domain = getWebDomain('US');
            window.location.href = `${domain}/ar/en/`;
          }
        });
      }
    }

    return (
      <Container isEditor={isEditor}>
        <Header2
          scrollEffect={scroll_effect}
          colorTheme={color_theme}
          logoLink={logo_link}
          drawerLinks={mapLoggedInLabel(drawerLinks, isLoggedIn)}
          centerLinks={centerLinks}
          logoType={logoType}
          zIndex={zIndex.MENU_BAR}
          drawerZIndex={zIndex.MENU_BAR + 1}
          transparent={transparent}
          float={float}
          spacer={spacer}
          background={background}
          button={button}
        />
      </Container>
    );
  }

  /** version 1 */
  return (
    <Container isEditor={isEditor}>
      <Header
        {...props}
        isLoggedIn={isLoggedIn}
        // prevent MenuBar going outside of editor (scroll_effect has position fixed)
        scroll_effect={isEditor ? false : props.scroll_effect}
      />
    </Container>
  );
}

const isRootPath = (url) => {
  return url.pathname === '/';
}

const containsArabicPath = (url) => {
  return url.pathname.includes('/ar/');
}

const shouldIgnoreRedirect = (url, props) => {
  // Ignore redirect when editing
  const isEditing = props.editor;

  // Ignore redirect when previewing
  const valueOfV = url.searchParams.get('v');
  const isDraft = valueOfV === 'DRAFT';

  // Ignore redirect when publishing
  const valueOfPublish = url.searchParams.get('publish');
  const isPreviewPublish = valueOfPublish === 'true';

  // Ignore redirect if the url contains /ar/ segment
  const containsARSegment = url.pathname.includes('/ar/');

  const shouldIgnore = isEditing ||
    isDraft ||
    isPreviewPublish ||
    containsARSegment;

  return shouldIgnore;
}

const shouldRedirectToArabic = async () => {
  try {
    const isRedirectForAREnabled = getRedirectForAR() === 'true';
    const isArabicRegion = await isIPArabicRegion();
    return isRedirectForAREnabled && isArabicRegion;
  } catch (error) {
    console.error('Error checking region:', error);
    return false;
  }
}

const Container = styled.Box(({ isEditor }) => ({
  zIndex: zIndex.MENU_BAR,
  position: 'relative',
  width: '100%',

  /*
    Positioning for the Header under component browser + template browser.
    `position: fixed;` will cause the menu to go outside the edit area.
  */
  '.component-browser-wrapper > & #ff-ui-header, .template-browser-wrapper & #ff-ui-header': {
    position: 'absolute'
  },
  '& #ff-ui-header': isEditor && {
    position: 'absolute'
  },
}));
